import constants from '../../../constants.js';
import {
  ColorListSparkLineSetting as SparkLine,
  CMS_CONTRACT_AFFILIATIONS,
  COLOR_LIST_STATUS_TEXT_COLORS,
} from '../cms-config.js';
import { priceCents, safeColor, firstText } from '../cms-market-helpers.js';

const MAX_HISTORY_DAYS = 30;
const FULL_PRICE_CENTS = 100;
const RACE_STATUS_MAX_PRICE = { tossUp: 55, slightLean: 65, lean: 75 };

const SPARKLINE_PLOT = { left: 2, width: 60, bottom: 26, height: 22 };
const SETTLEMENT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

const array = (value) => (Array.isArray(value) ? value : []);

export function resolveColorListSettings(list, uuid, index) {
  return (
    array(list).find(
      (item) => item && (uuid ? item.regionUUID === uuid : true) && item.colorListIndex === index,
    ) || null
  );
}

// The page's shared market cache can hold another widget's copy of the market (a story's market tile
// markets), whose status and settlement fields aren't set unless it's in a Head to Head tile. Those
// come from the ColorList's own copy; prices stay live from the cache.
export function withOwnSettlement(live, own) {
  if (!live || live === own) return own;
  const ownContracts = new Map(array(own.contracts).map((c) => [Number(c.contractId), c]));
  return {
    ...live,
    status: own.status,
    contracts: array(live.contracts).map((contract) => {
      const mine = ownContracts.get(Number(contract.contractId));
      return mine
        ? {
            ...contract,
            isSettled: mine.isSettled,
            isWinner: mine.isWinner,
            settlementDate: mine.settlementDate,
          }
        : contract;
    }),
  };
}

export function historicalSparkline(rawPoints) {
  const days = new Map();
  for (const point of array(rawPoints)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(point?.date || '') || priceCents(point.price) === null)
      continue;
    const time = Date.parse(`${point.date}T00:00:00Z`);
    if (Number.isFinite(time)) days.set(point.date, { time, price: Number(point.price) });
  }
  const points = [...days.values()].sort((a, b) => a.time - b.time);
  if (points.length < 2) return null;
  const first = points[0],
    last = points.at(-1);
  const path = points
    .map((p, i) => {
      const x =
        SPARKLINE_PLOT.left +
        ((p.time - first.time) / (last.time - first.time)) * SPARKLINE_PLOT.width;
      const y = SPARKLINE_PLOT.bottom - p.price * SPARKLINE_PLOT.height;
      return `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  return { points, path, trend: Math.sign(last.price - first.price) };
}

function raceStatus(rows) {
  // Nothing has traded yet, so there's no leader to rate: the label says the market is new.
  if (rows.length && rows.every((row) => row.price === null))
    return { label: 'New Market', textColors: COLOR_LIST_STATUS_TEXT_COLORS.newMarket };
  const leaders = rows.filter((row) => row.isLeader);
  if (!leaders.length) return null;
  const leader = leaders[0];
  if (leader.price <= RACE_STATUS_MAX_PRICE.tossUp)
    return { label: 'Toss-up', textColors: COLOR_LIST_STATUS_TEXT_COLORS.tossUp };
  if (new Set(leaders.map((row) => row.affiliation)).size > 1) return null;
  // No party label can resolve a tie between different unaffiliated candidates.
  if (!leader.affiliation && new Set(leaders.map((row) => row.name)).size > 1) return null;
  const level =
    leader.price <= RACE_STATUS_MAX_PRICE.slightLean
      ? 0
      : leader.price <= RACE_STATUS_MAX_PRICE.lean
        ? 1
        : 2;
  return {
    label: `${['Slight Lean', 'Lean', 'Likely'][level]} ${CMS_CONTRACT_AFFILIATIONS[leader.affiliation]?.label || leader.name}`,
    textColors: COLOR_LIST_STATUS_TEXT_COLORS[leader.affiliation]?.[level] || [
      leader.color,
      leader.color,
    ],
  };
}

function outcomeRows(row, symbol) {
  const yes = {
    ...row,
    name: 'YES',
    subtitle: '',
    color: 'var(--contract-betslip-yes-border-color)',
    affiliation: 0,
    dimImage: false,
  };
  const noPrice = row.price === null ? null : FULL_PRICE_CENTS - row.price;
  const noQuote = priceCents(row.contract.bestNoPrice);
  // Once settled, NO is the winner when the contract lost.
  const settled = row.isWinner || row.isDefeated;
  const noWon = settled && row.contract.isWinner === false;
  const no = {
    ...yes,
    name: 'NO',
    direction: 'no',
    color: 'var(--contract-betslip-no-border-color)',
    isWinner: noWon,
    isDefeated: settled && !noWon,
    price: noPrice,
    display: noPrice === null ? 'NEW' : `${noPrice}${symbol}`,
    buyPrice: noQuote > 0 ? noQuote : null,
    change: row.change === null ? null : row.change === 0 ? 0 : -row.change,
    sparkline: row.sparkline
      ? historicalSparkline(
          row.sparkline.points.map(({ time, price }) => ({
            date: new Date(time).toISOString().split('T')[0],
            price: 1 - price,
          })),
        )
      : null,
  };
  const max = row.price === null ? null : Math.max(row.price, noPrice);
  return [yes, no].map((side) => ({
    ...side,
    isLeader: side.price !== null && side.price === max,
  }));
}

function bidAskSpread(contract) {
  if (priceCents(contract?.bestYesPrice) === null || priceCents(contract?.bestNoPrice) === null)
    return null;
  return Math.round(
    (Number(contract.bestYesPrice) + Number(contract.bestNoPrice) - 1) * FULL_PRICE_CENTS,
  );
}

function settlementLabel(rows) {
  const winner = rows.find((row) => row.isWinner);
  if (!winner) return 'Winner Not Shown';
  const date = winner.contract.settlementDate;
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Settled';
  const day = new Date(`${date}T00:00:00Z`);
  if (!Number.isFinite(day.getTime()) || day.toISOString().split('T')[0] !== date) return 'Settled';
  return `Settled ${SETTLEMENT_DATE_FORMAT.format(day)}`;
}

export function buildColorList(settings, market, data, showOdds) {
  if (!settings || !market) return null;
  const symbol = showOdds ? '%' : '¢';
  const singleOutcome = array(settings.contracts).length === 1;
  const state =
    market.status === constants.MARKET_SETTLED
      ? constants.MARKET_SETTLED
      : market.status === constants.MARKET_CLOSED
        ? constants.MARKET_CLOSED
        : constants.MARKET_OPEN;
  const custom = settings.sparkLineSetting === SparkLine.Custom;
  const numDays = Math.min(
    MAX_HISTORY_DAYS,
    Math.max(1, Number(data?.numDays || settings.sparkLineNumDays || MAX_HISTORY_DAYS)),
  );
  const rows = array(settings.contracts).flatMap((meta) => {
    const contract = array(market.contracts).find((c) => c.contractId === Number(meta.id));
    if (!contract) return [];
    const history = array(data?.contracts).find((c) => c.contractId === contract.contractId);
    const party = CMS_CONTRACT_AFFILIATIONS[meta.affiliation];
    const lastPrice = priceCents(contract.lastTradePrice);
    const price = state === constants.MARKET_SETTLED || lastPrice > 0 ? lastPrice : null;
    const baseline = priceCents(custom ? history?.baselinePrice : contract.lastClosePrice);
    const quote = priceCents(contract.bestYesPrice);
    return [
      {
        id: contract.contractId,
        direction: 'yes',
        contract,
        isWinner: state === constants.MARKET_SETTLED && contract.isWinner === true,
        isDefeated: state === constants.MARKET_SETTLED && contract.isWinner !== true,
        name: firstText(meta.name, contract.contractShortName, contract.contractName),
        subtitle: [
          firstText(meta.subtitle, party?.name),
          meta.isIncumbent === true ? 'Incumbent' : '',
        ]
          .filter(Boolean)
          .join(' · '),
        image: firstText(meta.imagePath, contract.contractImageUrl),
        overlay: firstText(meta.imageOverlayText),
        color: safeColor(meta.colorHex, party?.color || '#6B7280'),
        affiliation: party ? Number(meta.affiliation) : 0,
        isIncumbent: meta.isIncumbent === true,
        price,
        display: price === null ? 'NEW' : `${price}${symbol}`,
        buyPrice: quote > 0 ? quote : null,
        change: price === null || baseline === null ? null : price - baseline,
        sparkline:
          settings.sparkLineSetting === SparkLine.None
            ? null
            : historicalSparkline(history?.points),
      },
    ];
  });
  const prices = rows.filter((row) => row.price !== null).map((row) => row.price);
  const max = prices.length ? Math.max(...prices) : null;
  rows.forEach((row) => {
    row.isLeader = row.price !== null && row.price === max;
  });
  const visibleRows = singleOutcome && rows.length === 1 ? outcomeRows(rows[0], symbol) : rows;
  const totalContractCount =
    Number.isSafeInteger(data?.totalContractCount) && data.totalContractCount >= rows.length
      ? data.totalContractCount
      : rows.length;
  // A single contract that hasn't traded yet has no Bid/Ask Spread to show.
  const newOutcome = singleOutcome && rows.length > 0 && rows[0].price === null;
  // Two prices make an Odds Spread only when they're the whole market, not two of its contracts.
  const spread = singleOutcome
    ? bidAskSpread(rows[0]?.contract)
    : rows.length === 2 && prices.length === 2 && totalContractCount === rows.length
      ? Math.abs(prices[0] - prices[1])
      : null;
  return {
    state,
    settlementLabel: state === constants.MARKET_SETTLED ? settlementLabel(visibleRows) : '',
    title: firstText(settings.market?.name, market.marketName),
    shortName: firstText(settings.market?.marketPsuedoShortname),
    date: firstText(settings.market?.eventDate),
    summaryText: firstText(settings.market?.contestantSummaryText),
    symbol,
    singleOutcome,
    numDays,
    changeLabel: custom ? `${numDays}d` : '',
    rows: visibleRows,
    moreLabel: totalContractCount > rows.length ? `${totalContractCount - rows.length} More` : '',
    raceStatus:
      state === constants.MARKET_SETTLED || settings.showRaceStatus === false
        ? null
        : raceStatus(singleOutcome ? visibleRows : rows),
    spreadLabel: singleOutcome ? 'Bid/Ask Spread' : 'Odds Spread',
    spreadSymbol: singleOutcome ? '¢' : symbol,
    spread,
    showSpread: singleOutcome ? !newOutcome : spread !== null,
  };
}
